/**
 * UCWonderFooter — the lightweight brand footer used everywhere on the
 * UCWonder brand (landing page, dashboard, planner pages) so the site
 * feels consistent. Homesbyli keeps the full Footer.jsx.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBrand } from '../brand/useBrand';

const PINE = '#1E3F70';

const FooterCol = ({ title, children }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500 mb-3">{title}</div>
    <ul className="space-y-2">{children}</ul>
  </div>
);

const FooterLink = ({ to, href, children }) => {
  const navigate = useNavigate();
  if (href) {
    return (
      <li>
        <a href={href} className="text-sm text-gray-700 hover:text-gray-900">{children}</a>
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        onClick={() => navigate(to)}
        className="text-sm text-gray-700 hover:text-gray-900 text-left"
      >
        {children}
      </button>
    </li>
  );
};

const UCWonderFooter = () => {
  const { brand } = useBrand();
  return (
    <footer className="border-t border-gray-200" style={{ background: '#fff' }} data-testid="ucwonder-footer">
      <div className="max-w-7xl mx-auto px-5 py-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <div>
          <div className="font-bold text-lg" style={{ color: PINE }}>{brand.displayName}</div>
          <p className="mt-2 text-xs text-gray-500 leading-relaxed">{brand.descriptor}</p>
        </div>
        <FooterCol title="Shop">
          <FooterLink to="/dining">Perks</FooterLink>
          <FooterLink to="/experiences">Experiences</FooterLink>
          <FooterLink to="/plan">UCWonder AI</FooterLink>
        </FooterCol>
        <FooterCol title="Travel">
          <FooterLink to="/plan">Plan a trip</FooterLink>
          <FooterLink to="/dashboard">UC Passport</FooterLink>
        </FooterCol>
        <FooterCol title="Company">
          <FooterLink to="/about">About us</FooterLink>
          <FooterLink to="/community">Community</FooterLink>
          <FooterLink href={`mailto:${brand.supportEmail}`}>Support</FooterLink>
          <FooterLink to="/privacy">Privacy policy</FooterLink>
          <FooterLink to="/terms">Terms of service</FooterLink>
        </FooterCol>
      </div>
      <div className="border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-5 py-4 text-xs text-gray-500 flex flex-wrap items-center justify-between gap-2">
          <span data-testid="footer-legal-line">
            © {new Date().getFullYear()} {brand.legalName}
            {brand.neq ? ` | NEQ: ${brand.neq}` : ''}
          </span>
          <span>Made in Montreal</span>
        </div>
        <div className="max-w-7xl mx-auto px-5 pb-1 text-[11px] text-gray-500 font-medium" data-testid="footer-compliance-line">
          <Link to="/data-security" className="underline hover:text-gray-700" data-testid="footer-compliance-link">Global Business Compliance Protocol &amp; Data Privacy Policy</Link>. · <a href={`mailto:${brand.supportEmail}`} className="underline hover:text-gray-700">{brand.supportEmail}</a>
        </div>
        <div className="max-w-7xl mx-auto px-5 pb-4 text-[11px] text-gray-400" data-testid="affiliate-disclosure-footer">
          {brand.displayName} partners with hotel and travel brands. Some links are affiliate links, which help keep our concierge free.
        </div>
      </div>
    </footer>
  );
};

export default UCWonderFooter;
